import React, { useEffect, useRef } from 'react';
import { isNative } from '../../utils/platform';

/**
 * One-time scroll reveal — no React state updates on scroll (prevents flicker).
 */
const ScrollReveal = ({
  children,
  direction = 'left',
  className = '',
  as: Component = 'div',
  delayMs = 0,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const skipMotion =
      isNative || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (skipMotion) {
      return undefined;
    }

    const directionClass =
      direction === 'right' ? 'pf-scroll-pod--from-right' : 'pf-scroll-pod--from-left';
    node.classList.add('pf-scroll-pod', directionClass);
    if (delayMs) node.style.animationDelay = `${delayMs}ms`;

    let revealed = false;

    const reveal = () => {
      if (revealed) return;
      revealed = true;
      node.classList.add('pf-scroll-pod--visible');
      observer.disconnect();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) reveal();
      },
      { threshold: 0.08, rootMargin: '0px 0px -5% 0px' }
    );

    observer.observe(node);

    const rect = node.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh && rect.bottom > 0) {
      reveal();
    }

    return () => observer.disconnect();
  }, [direction, delayMs]);

  return (
    <Component ref={ref} className={className || undefined}>
      {children}
    </Component>
  );
};

export default ScrollReveal;
